import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import { AuthProvider } from "./auth/AuthContext";
import { Toaster } from "./components/ui/sonner";
import App from "./App";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A partner panel is an operational tool: stale numbers on a dashboard are
      // worse than a spinner, and the data is cheap (indexed counts, not rows).
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * antd theme — a mirror of `src/index.css`, not a second design system.
 *
 * Every number here has a twin in the `@theme` block: the same petrol blue, the
 * same 8px radius, the same 38px control height, the same Inter face, the same
 * hairline. That is the whole point. The panel used to ask for `borderRadius:
 * 10` here while Tailwind drew `4px` corners and shadcn drew `12px`, so a
 * primary button and an antd button on one screen disagreed about their own
 * shape — which is what made the app read as assembled from three kits.
 *
 * Change a token in `index.css` and change it here in the same commit.
 */
const FONT_SANS =
  '"Inter Variable", "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const antdTheme = {
  token: {
    colorPrimary: "#2563eb",
    colorPrimaryHover: "#3b82f6",
    colorPrimaryActive: "#1d4ed8",
    colorSuccess: "#16a34a",
    colorWarning: "#f59e0b",
    colorError: "#dc2626",
    colorInfo: "#2563eb",
    colorLink: "#2563eb",
    colorText: "#0f172a",
    colorTextSecondary: "#475569",
    colorTextTertiary: "#94a3b8",
    colorBorder: "#e2e8f0",
    colorBorderSecondary: "#e2e8f0",
    colorBgContainer: "#ffffff",
    colorBgLayout: "transparent",
    borderRadius: 8,
    borderRadiusSM: 6,
    borderRadiusLG: 8,
    borderRadiusXS: 4,
    fontFamily: FONT_SANS,
    fontSize: 14,
    // 38px, not antd's 32 and not the old 40: it is the height of a `.field` in
    // index.css, so an antd control and a hand-rolled one sit on the same line.
    controlHeight: 38,
    lineWidth: 1,
    wireframe: false,
  },
  components: {
    Button: {
      controlHeight: 38,
      // 32, not antd's 24: `size="small"` is only used for table row actions, and
      // a 24px button next to 13px text is a hit target you can miss.
      controlHeightSM: 32,
      paddingInlineSM: 10,
      fontWeight: 500,
      primaryShadow: "none",
      defaultShadow: "none",
    },
    Input: { controlHeight: 38, paddingInline: 12 },
    Select: { controlHeight: 38 },
    InputNumber: { controlHeight: 38 },
    Card: { borderRadiusLG: 8, headerBg: "#ffffff", paddingLG: 20, headerHeight: 48 },
    Table: {
      headerBg: "#f8fafc",
      headerColor: "#475569",
      headerSplitColor: "transparent",
      rowHoverBg: "#eff6ff",
      borderColor: "#e2e8f0",
      cellPaddingBlock: 12,
      headerBorderRadius: 0,
    },
    Tag: { borderRadiusSM: 6, defaultBg: "#f1f5f9", defaultColor: "#64748b" },
    Menu: { itemHeight: 38, itemBorderRadius: 8, itemMarginInline: 0, itemMarginBlock: 2 },
    Modal: { borderRadiusLG: 12, titleFontSize: 16 },
    Form: { labelColor: "#475569", labelFontSize: 12, verticalLabelPadding: "0 0 6px" },
    Layout: { bodyBg: "transparent", siderBg: "transparent" },
    Upload: { colorBorder: "#cbd5e1" },
  },
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ConfigProvider theme={antdTheme}>
          <AuthProvider>
            <App />
            <Toaster position="bottom-right" closeButton />
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
